import { PageVisitTracker } from "@/analytics/PageVisitTracker";
import { useConfig } from "@/config/ConfigContext";
import { setBrandColors } from "@/hooks/useBrandColors";
import { RootState } from "@/store";
import { userAccountApi } from "@/store/userAccountApiSlice";
import { updateOnboardingProgress } from "@/store/userSlice";
import React, { useCallback, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useIdleTimer } from "react-idle-timer";
import { useDispatch, useSelector } from "react-redux";

import AdditionalDataModal from "../Modals/AdditionalDataModal";
import LogoutWarningModal from "../Modals/LogoutWarningModal";
import Footer from "../Navigation/Footer";
import { IframeModal } from "../Navigation/IframeModal";
import Navbar from "../Navigation/Navbar";

interface Proptypes {
  children: React.ReactNode;
}

export const IDLE_TIMEOUT = 1000 * 60 * 10; // 10 minutes

export const IDLE_MODAL_SEEN_KEY = "idleModalSeen";

const ContentWrapper: React.FC<Proptypes> = ({ children }) => {
  const [idleModalOpen, setIdleModalOpen] = React.useState(false);
  const [message, setMessage] = useState("");
  const [waitDuration] = useState(5);
  const [isIframeModalOpen, setIsIframeModalOpen] = useState(false);
  const [iframeToken, setIframeToken] = useState("");
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const appConfig = useConfig();

  const user = useSelector((state: RootState) => state.user);

  const isAuthenticated = (): boolean => !!user.id;

  // const hexToRgb = (hex?: string) => {
  //   if (!hex) return null;
  //   const sanitized = hex.replace("#", "");
  //   const full =
  //     sanitized.length === 3
  //       ? sanitized
  //           .split("")
  //           .map((c) => c + c)
  //           .join("")
  //       : sanitized;
  //   const int = parseInt(full, 16);
  //   return {
  //     r: (int >> 16) & 255,
  //     g: (int >> 8) & 255,
  //     b: int & 255,
  //   };
  // };

  // const lightenHex = (hex?: string, weight = 0.8) => {
  //   const rgb = hexToRgb(hex);
  //   if (!rgb) return defaultLight;
  //   const r = Math.round(rgb.r + (255 - rgb.r) * weight);
  //   const g = Math.round(rgb.g + (255 - rgb.g) * weight);
  //   const b = Math.round(rgb.b + (255 - rgb.b) * weight);
  //   return `rgb(${r}, ${g}, ${b})`;
  // };

  const handleOnIdle = () => {
    setMessage(t("logout_warning_modal.session_timeout_warning_message"));
    setIdleModalOpen(true);
  };

  useIdleTimer({
    timeout: IDLE_TIMEOUT,
    onIdle: handleOnIdle,
    // debounce: 500,
  });

  const handleOpenIframeModal = (token: string) => {
    setIframeToken(token);
    setIsIframeModalOpen(true);
  };

  const onAccountOpeningResult = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (result: any) => {
      const status = result?.status;

      if (typeof status === "string") {
        dispatch(updateOnboardingProgress(status));
        dispatch(
          userAccountApi.util.invalidateTags(["AdditionalDataRequirement"]),
        );
      }
    },
    [dispatch],
  );

  const onStatusUpdate = useCallback(
    (status: string) => {
      if (status) {
        dispatch(updateOnboardingProgress(status));
        dispatch(
          userAccountApi.util.invalidateTags(["AdditionalDataRequirement"]),
        );
      }
    },
    [dispatch],
  );

  const handleCloseIframeModal = useCallback(() => {
    setIsIframeModalOpen(false);
  }, []);

  // Auth cookies are HttpOnly — the token cannot be passed as a query param.
  // The iframe relies on the browser automatically sending the auth cookie
  // for same-origin requests (or a future dedicated short-lived token endpoint).
  const getIframeSrc = (): string => {
    return `${appConfig.VITE_APP_ADDITIONAL_DATA_BASE_URL}/#/alpaca`;
  };

  useLayoutEffect(() => {
    if (window.partner) {
      const partner = window.partner;
      document.title = partner.title + " Employee Portal";
      setBrandColors({ brandColor: partner.colors.primary });
      const favicon = document.querySelector(
        'link[rel="icon"]',
      ) as HTMLLinkElement;

      if (favicon && partner.favicon) {
        favicon.href = partner.favicon;
      }
    }
  }, []);
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <PageVisitTracker />

      {idleModalOpen && isAuthenticated() && (
        <LogoutWarningModal
          isOpen={idleModalOpen}
          message={message}
          countdownSeconds={waitDuration * 60}
          okHandler={() => {
            setIdleModalOpen(false);
          }}
        />
      )}

      {!isIframeModalOpen && (
        <AdditionalDataModal onOpenIframe={handleOpenIframeModal} />
      )}

      {isIframeModalOpen && (
        <IframeModal
          open={isIframeModalOpen}
          onClose={handleCloseIframeModal}
          iframeSrc={getIframeSrc()}
          token={iframeToken}
          onStatusUpdate={onStatusUpdate}
          onAccountOpeningResult={onAccountOpeningResult}
        />
      )}

      <main
        style={{
          //  background: `linear-gradient(180deg, #EEFCFC 0%, #FFF 100%), linear-gradient(90deg, #EEFCFC 0%, #FFF 100%)`,
          background: `linear-gradient(180deg, var(--sootchy-bg-75) 0%, #FFF 100%), linear-gradient(90deg, var(--sootchy-bg-75) 0%, #FFF 100%)`,
          //background: `linear-gradient(180deg, #F9F9F9 0%, #FFF 100%), linear-gradient(180deg, #EEFCFC 0%, #FFF 100%), #D9D9D9`,
        }}
        className="flex-grow overflow-hidden"
      >
        <div>{children}</div>
      </main>
      <Footer />
    </div>
  );
};

export default ContentWrapper;
